import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { projectData } from '@/data/project';

interface PlanExplainerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBlockUnit: () => void;
}

const stages = [
  {
    label: 'Stage 1 — On Booking',
    lines: ['Pay 30% of the unit value.', 'Block your preferred floor and view.'],
  },
  {
    label: 'Stage 2 — Construction Period',
    lines: ['No further payments from you.', 'No pre-EMI burden during construction.'],
  },
  {
    label: 'Stage 3 — On Possession (Dec 2030)',
    lines: ['Pay the remaining 70%.', 'Move into your forest-deck home.'],
  },
];

const PlanExplainerModal = ({ isOpen, onClose, onBlockUnit }: PlanExplainerModalProps) => {
  const reraNumber = projectData?.rera?.number ?? 'P51800002500059';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-[520px] w-[calc(100vw-2rem)] sm:w-full p-0 overflow-hidden border-0"
        style={{
          background: 'rgba(10, 10, 10, 0.96)',
          backdropFilter: 'blur(24px)',
          border: '1px solid hsl(var(--gold) / 0.35)',
          borderRadius: '12px',
        }}
      >
        <div className="px-6 sm:px-8 pt-7 pb-6">
          <DialogHeader>
            <DialogTitle
              className="text-white text-left"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(1.35rem, 3vw, 1.65rem)',
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              How the <span style={{ color: 'hsl(var(--gold-light))' }}>30:70</span> Subvention Plan Works
            </DialogTitle>
          </DialogHeader>

          {/* Stages */}
          <div className="mt-6 space-y-5" style={{ fontFamily: "'DM Sans', 'Inter', sans-serif" }}>
            {stages.map((stage) => (
              <div key={stage.label}>
                <p
                  className="uppercase tracking-[0.12em] mb-1.5"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'hsl(var(--gold-light))',
                  }}
                >
                  {stage.label}
                </p>
                {stage.lines.map((line) => (
                  <p key={line} className="text-white/90 leading-snug" style={{ fontSize: '14px' }}>
                    {line}
                  </p>
                ))}
              </div>
            ))}
          </div>

          {/* CTA */}
          <Button
            onClick={() => {
              onClose();
              onBlockUnit();
            }}
            className="w-full mt-7 h-auto py-4 rounded-lg font-semibold"
            style={{
              background: 'linear-gradient(135deg, hsl(35, 65%, 45%), hsl(42, 65%, 55%))',
              color: '#fff',
              border: 'none',
              fontSize: '15px',
            }}
          >
            Block My Unit
            <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
          </Button>

          {/* Disclaimer */}
          <p
            className="mt-5 text-white/55 leading-relaxed"
            style={{ fontSize: '11px', fontFamily: "'DM Sans', 'Inter', sans-serif" }}
          >
            Final unit pricing under the 30:70 subvention plan, payment schedule, bank
            approval, and applicable charges depend on the unit selected, applicant profile,
            and final agreement. Pricing differs from outright purchase. Subject to RERA
            conditions. Nahar Chandi Valley — MahaRERA No. {reraNumber}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanExplainerModal;
